import { CONTACT, IMAGES } from "@/lib/site-data";
import { eur, dstr, vatLabel } from "./shared";

const RED = "#e2001a";

export const MonthlyReport = ({ year, month, transactions, receipts, invoices }) => {
  const invMap = Object.fromEntries((invoices || []).map((i) => [i.id, i.number]));
  const income = transactions.filter((t) => t.kind === "einnahme");
  const expense = transactions.filter((t) => t.kind === "ausgabe");
  const sum = (arr) => arr.reduce((s, t) => s + t.amount, 0);
  const sumVat = (arr) => arr.reduce((s, t) => s + t.amount * (t.vat_rate || 0) / 100, 0);
  const netIn = sum(income), netOut = sum(expense);
  const ust = sumVat(income), vst = sumVat(expense);
  const label = `${String(month).padStart(2, "0")}/${year}`;

  return (
    <div className="print-sheet bg-white text-[#1a1d20]" data-testid="monthly-report" style={{ fontFamily: "Manrope, Arial, sans-serif" }}>
      <div className="flex items-start justify-between px-10 pt-8 pb-5">
        <div style={{ background: "#23272b", borderRadius: 10, padding: "10px 14px" }}>
          <img src={IMAGES.logo} alt="Ringel Fenstertechnik" style={{ height: 78 }} />
        </div>
        <div className="text-right text-xs leading-relaxed text-neutral-600">
          <p className="font-bold text-sm text-[#1a1d20]" style={{ fontFamily: "Outfit, sans-serif" }}>Ringel Fenstertechnik</p>
          <p>Inhaber: {CONTACT.owner}</p>
          <p>Ostkamp 50 · 59174 Kamen</p>
        </div>
      </div>

      <div className="px-10">
        <h1 className="text-2xl font-bold tracking-wide mb-1" style={{ fontFamily: "Outfit, sans-serif", color: RED }}>MONATSBERICHT {label}</h1>
        <p className="text-xs text-neutral-500 mb-6">Einnahmen-Überschuss-Rechnung · erstellt am {dstr(new Date().toISOString())}</p>

        {/* Summen */}
        <table className="w-full text-sm mb-8" style={{ borderCollapse: "collapse" }}>
          <tbody>
            {[
              ["Einnahmen (netto)", eur(netIn)],
              ["Ausgaben (netto)", eur(netOut)],
              ["Gewinn vor Steuern", eur(netIn - netOut)],
              ["Umsatzsteuer (aus Einnahmen)", eur(ust)],
              ["Vorsteuer (aus Ausgaben)", eur(vst)],
              ["USt-Zahllast", eur(ust - vst)],
            ].map(([k, v], i) => (
              <tr key={k} className={i === 2 || i === 5 ? "border-t-2 font-bold" : "border-b border-neutral-200"} style={i === 2 || i === 5 ? { borderColor: RED } : {}}>
                <td className="py-1.5 text-neutral-600">{k}</td>
                <td className="py-1.5 text-right font-semibold" style={i === 2 ? { color: RED } : {}}>{v}</td>
              </tr>
            ))}
          </tbody>
        </table>

        {/* Buchungen */}
        <p className="text-xs uppercase tracking-widest mb-2" style={{ color: RED }}>Buchungen ({transactions.length})</p>
        <table className="w-full text-xs" style={{ borderCollapse: "collapse" }}>
          <thead>
            <tr className="text-left uppercase tracking-widest text-neutral-500">
              <th className="pb-1.5 pr-2 border-b border-neutral-300">Datum</th>
              <th className="pb-1.5 pr-2 border-b border-neutral-300">Art</th>
              <th className="pb-1.5 pr-2 border-b border-neutral-300">Beschreibung</th>
              <th className="pb-1.5 pr-2 border-b border-neutral-300">Kategorie</th>
              <th className="pb-1.5 pr-2 border-b border-neutral-300 text-right">Netto</th>
              <th className="pb-1.5 pr-2 border-b border-neutral-300 text-right">MwSt</th>
              <th className="pb-1.5 border-b border-neutral-300 text-right">Brutto</th>
              <th className="pb-1.5 pl-2 border-b border-neutral-300">Rechnung</th>
            </tr>
          </thead>
          <tbody>
            {transactions.map((t) => (
              <tr key={t.id} className="border-b border-neutral-100">
                <td className="py-1.5 pr-2 text-neutral-500">{dstr(t.date)}</td>
                <td className="py-1.5 pr-2">{t.kind === "einnahme" ? "Einnahme" : "Ausgabe"}</td>
                <td className="py-1.5 pr-2">{t.description}</td>
                <td className="py-1.5 pr-2 text-neutral-500">{t.category || "–"}</td>
                <td className="py-1.5 pr-2 text-right">{eur(t.amount)}</td>
                <td className="py-1.5 pr-2 text-right text-neutral-500">{eur(t.amount * (t.vat_rate || 0) / 100)}</td>
                <td className="py-1.5 text-right font-semibold">{eur(t.amount * (1 + (t.vat_rate || 0) / 100))}</td>
                <td className="py-1.5 pl-2 text-neutral-500">{invMap[t.invoice_id] || "–"}</td>
              </tr>
            ))}
            {transactions.length === 0 && (
              <tr><td colSpan={8} className="py-4 text-center text-neutral-400">Keine Buchungen in diesem Monat.</td></tr>
            )}
          </tbody>
        </table>

        {/* Belege */}
        <p className="text-xs uppercase tracking-widest mt-8 mb-2" style={{ color: RED }}>Belege ({receipts.length})</p>
        <table className="w-full text-xs" style={{ borderCollapse: "collapse" }}>
          <tbody>
            {receipts.map((r) => (
              <tr key={r.id} className="border-b border-neutral-100">
                <td className="py-1.5 pr-2">{r.filename}</td>
                <td className="py-1.5 pr-2 text-neutral-500">{dstr(r.uploaded_at)}</td>
                <td className="py-1.5 text-neutral-500">
                  {r.transaction_id ? "Buchung zugeordnet" : "nicht zugeordnet"}
                  {r.invoice_id ? ` · Rechnung ${invMap[r.invoice_id] || ""}` : ""}
                </td>
              </tr>
            ))}
            {receipts.length === 0 && (
              <tr><td colSpan={3} className="py-4 text-center text-neutral-400">Keine Belege in diesem Monat hochgeladen.</td></tr>
            )}
          </tbody>
        </table>

        <p className="mt-8 text-[10px] text-neutral-400 border-t border-neutral-200 pt-3">
          Ringel Fenstertechnik · Inhaber {CONTACT.owner} · Ostkamp 50 · 59174 Kamen · {CONTACT.phone} · {CONTACT.email}
        </p>
      </div>
    </div>
  );
};
